import { useState } from "react";
import { Check, Camera, Factory } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { fmtDate } from "@/lib/format";
import { BigButton } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImagePicker, FileImage } from "@/components/files/FileFields";

export const STAGES = ["carpentry", "upholstery", "sewing", "finishing", "quality"];
export const progress = (o) => Math.round((STAGES.filter((k) => o.stages?.[k]?.done).length / STAGES.length) * 100);

export function ProgressBar({ o, small = false }) {
  const pct = progress(o);
  return (
    <div className={`w-full rounded-full bg-secondary overflow-hidden ${small ? "h-1.5" : "h-2.5"}`} data-testid="co-progress" data-pct={pct}>
      <div className="h-full bg-[#2B79C2] transition-[width]" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function ProductionDialog({ o, onClose, onSave }) {
  const { t, lang, staff } = useApp();
  const [stages, setStages] = useState(o.stages || {});
  const upd = (k, patch) => setStages((s) => ({ ...s, [k]: { ...(s[k] || {}), ...patch } }));
  const toggle = (k) => upd(k, stages[k]?.done ? { done: false, at: null, by: "" } : { done: true, at: new Date().toISOString(), by: staff?.name || "" });
  const pct = progress({ stages });
  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}><DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="production-dialog">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><Factory className="w-5 h-5" />{t("prod_title")} · #{o.number}</DialogTitle></DialogHeader>
      <div className="flex items-center gap-3"><ProgressBar o={{ stages }} /><b className="text-sm" dir="ltr" data-testid="prod-pct">{pct}%</b></div>
      <div className="space-y-2">
        {STAGES.map((k, i) => {
          const s = stages[k] || {};
          return (
            <div key={k} className={`rounded-xl border p-2 ${s.done ? "border-[#1F9D55] bg-[#F3FAF5]" : "border-border bg-white"}`} data-testid={`prod-stage-${k}`}>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => toggle(k)} className={`w-9 h-9 rounded-full grid place-items-center border-2 ${s.done ? "bg-[#1F9D55] border-[#1F9D55] text-white" : "border-border"}`} data-testid={`prod-toggle-${k}`}>{s.done ? <Check className="w-5 h-5" /> : <span className="text-xs font-bold">{i + 1}</span>}</button>
                <div className="flex-1"><div className="font-bold">{t(`stage_${k}`)}</div>{s.done && <div className="text-xs text-muted-foreground" dir="ltr">{fmtDate(s.at, lang)} · {s.by}</div>}</div>
                {s.photo_id && <span className="w-12 h-12 rounded-lg overflow-hidden"><FileImage id={s.photo_id} className="w-full h-full object-cover" /></span>}
              </div>
              <div className="mt-2 ps-11"><ImagePicker value={s.photo_id || ""} onChange={(id) => upd(k, { photo_id: id })} kind="attachment" testid={`prod-photo-${k}`} /></div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground flex items-center gap-1"><Camera className="w-3.5 h-3.5" />{t("prod_hint")}</p>
      <BigButton onClick={() => { onSave(stages); onClose(); }} className="w-full" data-testid="prod-save"><Check className="w-5 h-5" />{t("save")}</BigButton>
    </DialogContent></Dialog>
  );
}
